import { Fragment, useMemo, useState } from 'react';
import * as Checkbox from '@radix-ui/react-checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import { Check } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { DirectionType, IndexObjectType } from '@/types/api-types';

import IconChevronDown from '@/assets/svg/icon-chevron-down.svg?react';
import IconClose from '@/assets/svg/icon-close.svg?react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useAuthStore } from '@/stores/authStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useGlobalStore } from '@/stores/globalStore';

import {
  INDEX_OBJECT_TYPES,
  INSURANCE_AREA,
  USER_ROLES,
} from '@/constants/global-constants';

interface SecondStepFileUploadProps {
  handleUploadFiles: (agreementUuid: string, prePath: string) => void;
  filesToUploadLength: number;
  isUploading: boolean;
  hasInvalidFiles: boolean;
  hasOnlyDateErrors: boolean;
  ignoreDateErrors: boolean;
  setIgnoreDateErrors: (value: boolean) => void;
}

interface SecondStepFileUploadForm {
  directory: string;
  index: string;
  insurance_area: DirectionType;
  registration_plate?: string;
  vin_or_rno: string;
  insurance_object_type: IndexObjectType;
  agreement_name: string;
}

const SecondStepFileUpload = ({
  handleUploadFiles,
  filesToUploadLength,
  isUploading,
  hasInvalidFiles,
  hasOnlyDateErrors,
  ignoreDateErrors,
  setIgnoreDateErrors,
}: SecondStepFileUploadProps) => {
  const { user } = useAuthStore();
  const { findProcess, findFirstStep, indexes } = useGlobalStore();

  const visibleIndexes = useMemo(() => {
    return indexes.filter((index) => !index.is_deleted);
  }, [indexes]);

  const { createInsuranceObject, createAgreement } = useApiManagerStore();
  const {
    setInsuranceObjectsState,
    addAgreement,
    insuranceObjectsState,
    directoriesState,
  } = useFileExplorerStore();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const form = useForm<SecondStepFileUploadForm>({
    defaultValues: {
      directory:
        user?.role === USER_ROLES.EXECUTOR.value
          ? directoriesState.rootIds[0]
          : '',
      registration_plate: '',
      vin_or_rno: '',
      insurance_object_type: INDEX_OBJECT_TYPES.VEHICLE,
      agreement_name: '',
    },
    mode: 'onBlur',
  });

  // Watch form values for checkbox visibility
  const watchedInsuranceArea = form.watch('insurance_area');
  const watchedInsuranceObjectType = form.watch('insurance_object_type');

  // Checkbox should be visible only for "Страхування ТЗ"
  const showIgnoreDateErrorsCheckbox =
    watchedInsuranceArea === INSURANCE_AREA.INSURANCE &&
    watchedInsuranceObjectType === INDEX_OBJECT_TYPES.VEHICLE;

  const handleSubmitForm = async (formData: SecondStepFileUploadForm) => {
    try {
      const process = findProcess({
        index: formData.index,
        objectType: formData.insurance_object_type,
        direction: formData.insurance_area,
      });

      if (!process) {
        throw new Error('Process not found');
      }

      const firstStep = findFirstStep(process.steps);

      const insuranceObject = {
        directory: formData.directory,
        // TODO: make uppercase ?
        name: formData.vin_or_rno,
        insurance_object_type: formData.insurance_object_type,
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
      };

      const insuranceObjectUuid = await createInsuranceObject(insuranceObject);

      setInsuranceObjectsState({
        ...insuranceObjectsState,
        entities: {
          ...insuranceObjectsState.entities,
          [insuranceObjectUuid]: {
            ...insuranceObject,
            uuid: insuranceObjectUuid,
            breadcrumb_name: formData.vin_or_rno,
            parentId: formData.directory,
          },
        },
        rootIds: [...insuranceObjectsState.rootIds, insuranceObjectUuid],
      });

      const agreement = {
        insurance_object: insuranceObjectUuid,
        index: formData.index,
        name: formData.agreement_name?.trim(),
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
        creator: user?.uuid || '',
        registration_plate: formData.registration_plate?.trim() || '',
        assignee: user?.uuid || '',
        tag_set: [],
        process: process.uuid,
        step: firstStep.uuid,
        has_file_errors: false,
      };

      const agreementUuid = await createAgreement(agreement);

      addAgreement({
        ...agreement,
        uuid: agreementUuid,
        breadcrumb_name: formData.agreement_name,
        parentId: `${formData.insurance_area}_${insuranceObjectUuid}`,
        directory_name:
          directoriesState.entities[formData.directory]?.name ||
          formData.directory,
        has_file_errors: false,
      });

      const directoryName =
        directoriesState.entities[formData.directory]?.name ||
        formData.directory;

      const prePath = `${encodeURIComponent(directoryName)}/${encodeURIComponent(formData.vin_or_rno)}/${encodeURIComponent(agreementUuid)}`;

      await handleUploadFiles(agreementUuid, prePath);
    } catch (error) {}
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmitForm)}
        className="flex h-full flex-col justify-between overflow-y-auto rounded-md bg-white p-6 shadow-sm sm:w-1/3"
      >
        <div className="flex flex-col">
          <p className="mb-2.5 text-2xl font-medium">
            Крок №2: Інформація про файли
          </p>
          <p className="mb-5 text-base font-medium">
            Після завантаження файлу(ів) необхідно додати інформацію про об'єкт
            страхування до якого він (вони) відносяться
          </p>
          <FormField
            control={form.control}
            name="directory"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ''}
                    disabled={directoriesState.rootIds.length <= 1}
                  >
                    <SelectTrigger
                      disabled={directoriesState.rootIds.length <= 1}
                      className="mb-6 w-full"
                    >
                      <SelectValue placeholder="Оберіть дирекцію" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(directoriesState.entities).map(
                        (directory) => (
                          <SelectItem
                            key={directory.uuid}
                            value={directory.uuid}
                          >
                            <span>{directory.name}</span>
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="mb-6">
            <FormField
              control={form.control}
              name="insurance_object_type"
              rules={{ required: "Обов'язкове поле" }}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Tabs
                      defaultValue={INDEX_OBJECT_TYPES.VEHICLE}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.setValue('index', '', { shouldValidate: false });
                        form.setValue('vin_or_rno', '', {
                          shouldValidate: false,
                        });
                      }}
                    >
                      <div className="mb-6 flex items-center justify-between">
                        <span className="text-base font-medium">
                          Об'єкт страхування
                          <br />
                          або врегулювання
                        </span>
                        <TabsList
                          className={cn(
                            'grid h-[34px] w-[170px] grid-cols-2 rounded-md border border-gray-200',
                          )}
                        >
                          <TabsTrigger
                            className={cn(
                              'rounded-md font-medium text-gray-200 data-[state=active]:border data-[state=active]:border-primary data-[state=active]:text-primary',
                            )}
                            value={INDEX_OBJECT_TYPES.VEHICLE}
                          >
                            ТЗ
                          </TabsTrigger>
                          <TabsTrigger
                            className={cn(
                              'rounded-md font-medium text-gray-200 data-[state=active]:border data-[state=active]:border-primary data-[state=active]:text-primary',
                            )}
                            value={INDEX_OBJECT_TYPES.PROPERTY}
                          >
                            {INDEX_OBJECT_TYPES.PROPERTY}
                          </TabsTrigger>
                        </TabsList>
                      </div>

                      <TabsContent value={INDEX_OBJECT_TYPES.VEHICLE}>
                        <div className="space-y-6">
                          <FormField
                            control={form.control}
                            name="vin_or_rno"
                            rules={{
                              required: "Обов'язкове поле",
                              minLength: {
                                value: 17,
                                message: `Мінімальна довжина - 17 символів`,
                              },
                              maxLength: {
                                value: 17,
                                message: `Максимальна довжина - 17 символів`,
                              },
                            }}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input {...field} placeholder="VIN" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="registration_plate"
                            rules={{
                              required: "Обов'язкове поле",
                              pattern: {
                                value: /^[A-Za-z0-9]+$/,
                                message:
                                  'Будь ласка, використовуйте тільки латинські літери та цифри',
                              },
                            }}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder="Державний номерний знак"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </TabsContent>

                      <TabsContent value={INDEX_OBJECT_TYPES.PROPERTY}>
                        <FormField
                          control={form.control}
                          name="vin_or_rno"
                          rules={{
                            required: "Обов'язкове поле",
                            minLength: {
                              value: 8,
                              message: `Мінімальна довжина - 8 символів`,
                            },
                            maxLength: {
                              value: 10,
                              message: `Максимальна довжина - 10 символів`,
                            },
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input {...field} placeholder="РНОКПП/ЄДРПОУ" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </TabsContent>
                    </Tabs>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="insurance_area"
            rules={{ required: "Обов'язкове поле" }}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      key="insurance-area-trigger"
                      className="mb-6 w-full"
                    >
                      <SelectValue placeholder="Напрямок" />
                    </SelectTrigger>
                    <div key="insurance-area-content-wrapper">
                      <SelectContent>
                        {Object.values(INSURANCE_AREA).map((direction) => (
                          <Fragment key={`insurance-area-${direction}`}>
                            <SelectItem key={direction} value={direction}>
                              <span>{direction}</span>
                            </SelectItem>
                          </Fragment>
                        ))}
                      </SelectContent>
                    </div>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="agreement_name"
            rules={{ required: "Обов'язкове поле" }}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Номер договору/справи"
                    className="mb-6 w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="index"
            rules={{ required: "Обов'язкове поле" }}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Popover
                    key="index-popover"
                    open={dropdownOpen}
                    onOpenChange={(open) => {
                      setDropdownOpen(open);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <div
                        className={cn(
                          'relative z-20 flex min-h-[50px] min-w-full items-center justify-start rounded-md border border-gray-200 px-4 pr-10 text-left text-gray-200 focus-visible:outline-none',
                        )}
                      >
                        {field.value &&
                        visibleIndexes.find(
                          (index) => index.uuid === field.value,
                        ) ? (
                          <span className="text-base text-primary">
                            {`${visibleIndexes.find((index) => index.uuid === field.value)?.name} - ${visibleIndexes.find((index) => index.uuid === field.value)?.description}`}
                          </span>
                        ) : (
                          'Індекс'
                        )}
                        <button
                          type="button"
                          className="absolute left-0 top-0 -z-10 flex h-full w-full items-center justify-end pr-4 focus-visible:outline-none"
                        >
                          <IconChevronDown className="h-[10px] w-[20px] fill-transparent stroke-gray-200" />
                        </button>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="relative w-[var(--radix-popper-anchor-width)] min-w-full p-0">
                      <Command shouldFilter={false}>
                        <div className="relative flex w-full items-center border-b border-separator">
                          <CommandInput
                            placeholder="Пошук індексу"
                            className="h-[50px] pr-10 text-base"
                            value={searchTerm}
                            onValueChange={setSearchTerm}
                          />
                          {field.value && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                field.onChange('');
                              }}
                              className="absolute right-5 top-1/2 -translate-y-1/2"
                            >
                              <IconClose className="h-4 w-4 fill-gray-200" />
                            </button>
                          )}
                        </div>
                        <CommandList>
                          {visibleIndexes
                            .filter((index) => {
                              const matchesSearch = searchTerm
                                ? index.name
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase()) ||
                                  index.description
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase())
                                : true;

                              const selectedArea =
                                form.getValues('insurance_area');
                              const selectedType = form.getValues(
                                'insurance_object_type',
                              );

                              const matchesArea =
                                !selectedArea ||
                                index.insurance_area === selectedArea;
                              const matchesType =
                                !selectedType ||
                                index.insurance_object_type === selectedType;

                              return (
                                matchesSearch && matchesArea && matchesType
                              );
                            })
                            .map((index) => (
                              <div key={`index-item-wrapper-${index.uuid}`}>
                                <CommandItem
                                  key={index.uuid}
                                  value={index.uuid}
                                  onSelect={(value) => {
                                    const selectedIndex = visibleIndexes.find(
                                      (idx) => idx.uuid === value,
                                    );
                                    if (selectedIndex) {
                                      field.onChange(value);
                                      field.onBlur();
                                    }
                                    setDropdownOpen(false);
                                  }}
                                  className="min-h-[50px]"
                                >
                                  <Check
                                    className={cn(
                                      'h-4 w-4',
                                      field.value === index.uuid
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  <span>
                                    {index.name} - {index.description}
                                  </span>
                                </CommandItem>
                              </div>
                            ))}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {showIgnoreDateErrorsCheckbox && (
            <label className="mt-6 flex cursor-pointer items-center justify-between gap-2 pl-5">
              <span className="text-sm text-gray-200">
                Ігнорувати перевірку файлів з помилками
              </span>
              <Checkbox.Root
                className="flex h-12 w-12 items-center justify-center rounded-md border border-gray-200"
                checked={ignoreDateErrors}
                onCheckedChange={(checked) =>
                  setIgnoreDateErrors(checked === true)
                }
              >
                <Checkbox.Indicator>
                  <IconClose className="h-4 w-4" />
                </Checkbox.Indicator>
              </Checkbox.Root>
            </label>
          )}
        </div>
        <div className="mt-5 flex flex-col gap-3">
          <div className="flex justify-end">
            <Button
              variant="destructive"
              disabled={
                !form.formState.isValid ||
                filesToUploadLength === 0 ||
                form.formState.isSubmitting ||
                isUploading ||
                (hasInvalidFiles && !(ignoreDateErrors && hasOnlyDateErrors))
              }
              type="submit"
            >
              Завантажити
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default SecondStepFileUpload;
